import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import FullScreenModal from 'components/consumer/FullScreenModal';
import JBProductImage from 'components/essentials/JBImage/JBProductImage';
import IconMobileGrid1x from 'components/icons/iconMobileGrid1x';
import IconMobileGrid2x from 'components/icons/iconMobileGrid2x';
import RatioContainer from 'components/consumer/RatioContainer';
import PDPPinchToZoomGallery from 'components/consumer/PDP/PDPPinchToZoomGallery';
import ToggleTabGroup from 'components/consumer/ToggleTabGroup';
import SnackbarShowOnce from 'components/consumer/SnackbarShowOnce';
import getTabContentId from 'commons/getTabContentId';
import getTabId from 'commons/getTabId';
import withBrowserOnly from 'components/hoc/withBrowserOnly';
import useScreen from 'hooks/useScreen';
import useJumpToSelectedGalleryPhoto from './hooks/useJumpToSelectedGalleryPhoto';
import PDPFullScreenGalleryListItem from './PDPFullScreenGalleryListItem';

const PDPFullScreenGallery = ({
  className = '',
  displayShownIn = false,
  enableCrops = true,
  pdpIsModularBYOProduct = false,
  initialSelectedPhotoIndex = 0,
  isEditOrder = false,
  isOpen = false,
  onClose,
  photos = [],
  productName = '',
  showGridView = false,
}) => {
  const { isMobileOrTablet } = useScreen();

  const [mobileColumnsAcross, setMobileColumnsAcross] = useState(1);
  const is2MobileColumns = mobileColumnsAcross === 2;

  // PDPPinchToZoomGallery state
  const [isPinchGalleryOpen, setIsPinchGalleryOpen] = useState(false);
  const [pinchGalleryIndex, setPinchGalleryIndex] = useState(0);

  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(
    initialSelectedPhotoIndex
  );

  useEffect(() => {
    setSelectedPhotoIndex(initialSelectedPhotoIndex);
  }, [initialSelectedPhotoIndex]);

  const galleryRef = useJumpToSelectedGalleryPhoto({
    selectedPhotoIndex,
    isOpen,
  });

  const [selectedDesktopTab, setSelectedDesktopTab] = useState('Listed');

  const selectedTab = isMobileOrTablet ? 'Listed' : selectedDesktopTab;

  return (
    <FullScreenModal
      isOpen={isOpen}
      onClose={onClose}
      headerLeft={
        showGridView ? (
          <ToggleTabGroup
            selectedTab={getTabId(selectedTab, 'PDPFullScreenGallery')}
            noBottomBorder
            size="md"
            aria-label="Full Screen Gallery Grid"
            items={['Listed', 'Grid'].map(label => ({
              id: getTabId(label, 'PDPFullScreenGallery'),

              label,
              contentId: getTabContentId(label, 'PDPFullScreenGallery'),
              onClick: () => {
                setSelectedDesktopTab(label);
              },
            }))}
          />
        ) : (
          undefined
        )
      }
      mobileNoPadding
      mobileHeaderCenter={
        <span className="text-sm font-bold items-center text-gray flex h-full justify-center">
          {productName}
        </span>
      }
      mobileHeaderRight={
        <div
          className="items-center flex h-full"
          role="button"
          tabIndex="0"
          onClick={() => {
            setMobileColumnsAcross(is2MobileColumns ? 1 : 2);
          }}
        >
          {is2MobileColumns ? <IconMobileGrid2x /> : <IconMobileGrid1x />}
        </div>
      }
      mobileHeaderNoPointerEvents={isPinchGalleryOpen}
    >
      <FixedWidthContainer
        className={classNames('lg:mt-10 lg:mb-10', className)}
        noMobilePadding
      >
        <div
          aria-labelledby={getTabId('Listed', 'PDPFullScreenGallery')}
          hidden={selectedTab !== 'Listed' ? true : undefined}
          id={getTabContentId('Listed', 'PDPFullScreenGallery')}
          role="tabpanel"
        >
          {selectedTab === 'Listed' && (
            <div
              ref={galleryRef}
              className={classNames('grid lg:gap-8', {
                'gap-[.5rem] grid-cols-[repeat(auto-fit,minmax(90%,1fr))]':
                  mobileColumnsAcross !== 2,
                '!gap-[2px] !grid-cols-[repeat(auto-fit,minmax(45%,1fr))]':
                  mobileColumnsAcross === 2,
              })}
            >
              {photos.map(
                (
                  {
                    src,
                    id,
                    type,
                    optionId,
                    valueId,
                    upholsteryName,
                    cropData,
                    product_name = '',
                  },
                  photoIndex
                ) => (
                  <PDPFullScreenGalleryListItem
                    key={id}
                    isEditOrder={isEditOrder}
                    enableCrops={enableCrops}
                    type={type}
                    src={src}
                    is2MobileColumns={is2MobileColumns}
                    isMobile={isMobileOrTablet}
                    onClick={() => {
                      if (isMobileOrTablet) {
                        setPinchGalleryIndex(photoIndex);
                        setIsPinchGalleryOpen(true);
                      }
                    }}
                    optionId={optionId}
                    valueId={valueId}
                    upholsteryName={upholsteryName}
                    closeFullScreenGallery={onClose}
                    cropData={cropData}
                    productName={
                      pdpIsModularBYOProduct
                        ? product_name
                        : displayShownIn
                        ? productName
                        : null
                    }
                    displayShownIn={displayShownIn}
                  />
                )
              )}
            </div>
          )}
        </div>

        <div
          aria-labelledby={getTabId('Grid', 'PDPFullScreenGallery')}
          hidden={selectedTab !== 'Grid' ? true : undefined}
          id={getTabContentId('Grid', 'PDPFullScreenGallery')}
          role="tabpanel"
        >
          {selectedTab === 'Grid' && (
            <div
              className={classNames(
                'grid gap-6 grid-cols-[repeat(auto-fill,minmax(11rem,1fr))]',
                {
                  '!gap-[2px] !grid-cols-[repeat(auto-fit,minmax(45%,1fr))]':
                    mobileColumnsAcross === 2,
                }
              )}
            >
              {photos.map(({ src, id, type, cropData }, photoIndex) => (
                <RatioContainer key={id}>
                  <JBProductImage
                    className="block rounded-sm cursor-pointer"
                    src={src}
                    sizes="100vw"
                    enableCrops={enableCrops}
                    type={type}
                    ar="1:1"
                    cropFormat="1x1"
                    crops={cropData}
                    onClick={() => {
                      setSelectedDesktopTab('Listed');
                      setSelectedPhotoIndex(photoIndex);
                    }}
                    size="10vw"
                  />
                </RatioContainer>
              ))}
            </div>
          )}
        </div>

        {isMobileOrTablet && (
          <>
            <PDPPinchToZoomGallery
              isEditOrder={isEditOrder}
              isOpen={isPinchGalleryOpen}
              onClose={(closeGallery) => {
                setIsPinchGalleryOpen(false);
                if (closeGallery) {
                  onClose();
                }
              }}
              photos={photos}
              selectedPhotoIndex={pinchGalleryIndex}
              setSelectedPhotoIndex={setPinchGalleryIndex}
            />

            {!isPinchGalleryOpen && (
              <SnackbarShowOnce storageKey="tapToZoomInAndSeeTheMaterial">
                Tap to zoom in and
                <br />
                see the material.
              </SnackbarShowOnce>
            )}
          </>
        )}
      </FixedWidthContainer>
    </FullScreenModal>
  );
};

PDPFullScreenGallery.propTypes = {
  className: PropTypes.string,
  displayShownIn: PropTypes.bool,
  enableCrops: PropTypes.bool,
  pdpIsModularBYOProduct: PropTypes.bool,
  initialSelectedPhotoIndex: PropTypes.number,
  isEditOrder: PropTypes.bool,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      id: PropTypes.string,
      src: PropTypes.string,
    })
  ),
  productName: PropTypes.string,
  showGridView: PropTypes.bool,
};

export default withBrowserOnly(PDPFullScreenGallery);
